import { useEffect, useRef, useState } from "react";
import Reveal from "@/components/base/Reveal";
import { doctorIllustration, stethoscopeIcon } from "@/mocks/ppcare";

function CountUp({ end, suffix = "" }: { end: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          const duration = 1600;
          const start = performance.now();
          const tick = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(end * eased));
            if (progress < 1) {
              frame = requestAnimationFrame(tick);
            }
          };
          frame = requestAnimationFrame(tick);
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [end]);

  return (
    <span ref={ref}>
      {value}
      {suffix}
    </span>
  );
}

const stats = [
  { end: 99, suffix: "%", label: "of patients can book an appointment within just two business days" },
  { end: 700, suffix: "", label: "Verified 5 star Google reviews" },
  { end: 35, suffix: " Yrs", label: "Family owned and operated" },
];

export default function ResultsSection() {
  return (
    <section className="bg-primary-950 py-16 md:py-24">
      <div className="container">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <p className="mb-3 font-label text-sm font-medium uppercase tracking-[0.2em] text-accent-400">
              Personal Physician Care, P.A.
            </p>
            <h2 className="text-2xl font-bold leading-tight text-background-50 md:text-4xl">
              Partnering With You to Drive{" "}
              <span className="text-accent-400">Results</span>
            </h2>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div className="flex flex-col gap-6">
            {stats.map((stat, index) => (
              <Reveal key={stat.label} delay={index * 100}>
                <div className="flex items-center gap-6 rounded-lg border border-background-50/10 bg-background-50/5 p-6">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-accent-500/15">
                    <img src={stethoscopeIcon} alt="" className="h-9 w-9 object-contain" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-accent-400 md:text-4xl">
                      <CountUp end={stat.end} suffix={stat.suffix} />
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-background-200">
                      {stat.label}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="flex justify-center" delay={150}>
            <img
              src={doctorIllustration}
              alt="Doctor illustration"
              className="w-full max-w-md object-contain"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}